import { createContext, ReactNode, useContext, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { ProjectParams } from "src/routes/routesDef";
import { fail } from "src/utils";
import { DraftPlanScheduleQueryHookResult, useDraftPlanScheduleQuery } from "./ScheduleDraftMode.generated";
import { DraftScheduleStoreProvider, useDraftScheduleInput, useDraftScheduleStore } from "./scheduleDraftStore";

/**
 * Holds the draft for the draft schedule page and its publish workflow.
 *
 * The two pages use different chrome, so they are separate routes. The draft store and its simulated schedule sit
 * above both, so moving between them keeps the draft and doesn't simulate it again. Tests and stories pass the page
 * as `children` instead of rendering it through a route.
 */
export function DraftScheduleLayout(props: { children?: ReactNode }) {
  const { children } = props;
  const { name } = useProjectContext();
  return (
    <DraftScheduleStoreProvider scheduleParentName={name}>
      <DraftPlanScheduleProvider>{children ?? <Outlet />}</DraftPlanScheduleProvider>
    </DraftScheduleStoreProvider>
  );
}

/** The schedule simulated with the draft's changes, from the nearest `DraftScheduleLayout`. */
export function useDraftPlanSchedule(): DraftPlanScheduleQueryHookResult {
  return useContext(DraftPlanScheduleContext) ?? fail("Missing DraftScheduleLayout");
}

// The draft store holds the user's edits (mutable changes to the draft). This context holds the Apollo query result
// of those edits, so the draft page and the publish page share one query and its loading state.
const DraftPlanScheduleContext = createContext<DraftPlanScheduleQueryHookResult | undefined>(undefined);

function DraftPlanScheduleProvider(props: { children: ReactNode }) {
  const { children } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const input = useDraftScheduleInput(projectId);
  const maybeSetInitialValues = useDraftScheduleStore((state) => state.maybeSetInitialValues);

  const query = useDraftPlanScheduleQuery({ variables: { input, projectId } });

  useEffect(() => {
    if (query.data) maybeSetInitialValues(query.data);
  }, [maybeSetInitialValues, query.data]);

  return <DraftPlanScheduleContext.Provider value={query}>{children}</DraftPlanScheduleContext.Provider>;
}
